import React, { useEffect, useState } from 'react';
import { getDateFormattedShort } from '../../../utils/DateUtils';
import { orderSides } from '../../../utils/OrderUtils';

/**
 * props:
 * - data
 */
function ResultsTable(props) {

    const [backtest, setBacktest] = useState({});

    function getCoin(coin) {
        return coin && typeof coin === 'number' ? coin.toFixed(5) : coin;
    }

    function getPerc(value) {
        if (!value) return <React.Fragment></React.Fragment>;

        let bg;
        const perc = value && typeof value === 'number' ? value.toFixed(4) : value;

        if (value > 0) {
            value = '+' + perc + '%';
            bg = 'badge bg-success py-1 ms-1';
        }
        else if (value < 0) {
            value = perc + '%';
            bg = 'badge bg-danger py-1 ms-1';
        }
        else {
            value = '0%';
            bg = 'badge bg-secondary py-1 ms-1';
        }

        return <span className={bg}>{value}</span>;
    }

    function getItem(order) {
        const date = new Date(order.transactTime);
        return getDateFormattedShort(date);
    }

    function getMetricBadge(label, value, format, thresholds, lowerIsBetter = false) {
        if (value === undefined || value === null) return null;
        const formatted = format(value);
        let bg = 'bg-secondary';
        if (thresholds) {
            const cmp = lowerIsBetter ? -value : value;
            const good = lowerIsBetter ? -thresholds.good : thresholds.good;
            const ok = lowerIsBetter ? -thresholds.ok : thresholds.ok;
            if (cmp >= good) bg = 'bg-success';
            else if (cmp >= ok) bg = 'bg-warning text-dark';
            else bg = 'bg-danger';
        }
        return (
            <div className='col-md-3 mb-3 text-center'>
                <small className='d-block text-muted'>{label}</small>
                <span className={`badge ${bg} py-1 px-2`} style={{ fontSize: '0.9rem' }}>{formatted}</span>
            </div>
        );
    }

    function EquityCurve({ data }) {
        if (!data || data.length < 2) return null;

        const W = 600, H = 120, PAD = 8;
        const values = data.map(p => p.value);
        const times = data.map(p => p.time);
        const minV = Math.min(...values);
        const maxV = Math.max(...values);
        const minT = Math.min(...times);
        const maxT = Math.max(...times);
        const rangeV = maxV - minV || 1;
        const rangeT = maxT - minT || 1;

        const toX = t => PAD + ((t - minT) / rangeT) * (W - PAD * 2);
        const toY = v => H - PAD - ((v - minV) / rangeV) * (H - PAD * 2);

        const points = data.map(p => `${toX(p.time).toFixed(1)},${toY(p.value).toFixed(1)}`).join(' ');

        const startVal = values[0];
        const endVal = values[values.length - 1];
        const color = endVal >= startVal ? '#1cc88a' : '#e74a3b';

        const areaPoints = `${toX(minT).toFixed(1)},${H - PAD} ${points} ${toX(maxT).toFixed(1)},${H - PAD}`;

        return (
            <div className='col-12 mb-3'>
                <small className='d-block text-muted mb-1'>Equity Curve</small>
                <svg width='100%' viewBox={`0 0 ${W} ${H}`} style={{ background: '#f8f9fc', borderRadius: 4 }}>
                    <polygon points={areaPoints} fill={color} fillOpacity='0.15' />
                    <polyline points={points} fill='none' stroke={color} strokeWidth='2' />
                    <line x1={PAD} y1={toY(startVal)} x2={W - PAD} y2={toY(startVal)}
                        stroke='#858796' strokeWidth='1' strokeDasharray='4,3' />
                </svg>
            </div>
        );
    }

    useEffect(() => {
        if (!props.data) return;
        setBacktest(props.data);
    }, [props.data]);

    return (
        <React.Fragment>
            <div className='row'>
                <div className='col-md-6 mb-3'>
                    <b>Base Final ({backtest.base}): </b> <br /> {getCoin(backtest.endBase)} {getPerc(backtest.basePerc)}
                </div>
                <div className='col-md-6 mb-3'>
                    <b>Quote Final ({backtest.quote}): </b> <br /> {getCoin(backtest.endQuote)} {getPerc(backtest.quotePerc)}
                </div>
            </div>

            {/* Custo da IA-no-backtest: transparência sobre chamadas reais x cache x falhas fail-open. */}
            {backtest.aiBacktest && !backtest.aiBacktest.skipped && (
                <div className='row'>
                    <div className='col-12 mb-3'>
                        <div className='card border-left-info'>
                            <div className='card-body py-2'>
                                <div className='d-flex justify-content-between flex-wrap align-items-center'>
                                    <b>IA no backtest</b>
                                    <span>
                                        <span className='badge bg-info py-1 me-1' title='Chamadas reais ao provedor de IA (uma por payload distinto).'>{backtest.aiBacktest.calls} chamadas</span>
                                        <span className='badge bg-secondary py-1 me-1' title='Pontos servidos pelo cache (payload idêntico).'>{backtest.aiBacktest.cached} do cache</span>
                                        {backtest.aiBacktest.failed > 0 && <span className='badge bg-warning text-dark py-1 me-1' title='Chamadas que falharam (rate-limit/timeout) — ignoradas (fail-open).'>{backtest.aiBacktest.failed} falhas</span>}
                                        {backtest.aiBacktest.timedOut > 0 && <span className='badge bg-warning text-dark py-1 me-1' title='Pontos não avaliados por orçamento de tempo (provedor lento/rate-limit) — tratados como sem sinal (fail-open). Use um provedor com folga em Configurações → IA no backtest.'>{backtest.aiBacktest.timedOut} pulados (tempo)</span>}
                                    </span>
                                </div>
                                <small className='d-block text-muted mt-1'>
                                    {backtest.aiBacktest.sampled} pontos amostrados (cadência {backtest.aiBacktest.cadence}). Sinal reconstruído sobre os candles históricos, sem look-ahead.
                                </small>
                                {backtest.aiBacktest.signalDist && (
                                    <small className='d-block mt-1'>
                                        <span className='text-muted'>Sinais da IA: </span>
                                        <span className='text-success' title='Sinais BUY reconstruídos'>{backtest.aiBacktest.signalDist.buy} BUY</span>
                                        <span className='text-muted'> · </span>
                                        <span className='text-danger' title='Sinais SELL reconstruídos'>{backtest.aiBacktest.signalDist.sell} SELL</span>
                                        <span className='text-muted'> · {backtest.aiBacktest.signalDist.hold} HOLD</span>
                                        {backtest.aiBacktest.signalDist.avgConfidence != null && <span className='text-muted'> · conf. média {(backtest.aiBacktest.signalDist.avgConfidence * 100).toFixed(0)}%</span>}
                                        <span className='text-muted' title='Maior sequência de BUY/SELL consecutivos atingida na amostragem. Condições que exigem "N consecutivos" só disparam se este número alcançá-las — amostragem esparsa em janelas longas reduz o consecutivo.'> · máx. consec. {backtest.aiBacktest.signalDist.maxConsecutiveBuy}↑/{backtest.aiBacktest.signalDist.maxConsecutiveSell}↓</span>
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Métricas avançadas */}
            <div className='row text-center mb-2'>
                {getMetricBadge('Win Rate', backtest.winRate, v => v.toFixed(1) + '%',
                    { good: 55, ok: 45 })}
                {getMetricBadge('Max Drawdown', backtest.maxDrawdown, v => '-' + v.toFixed(2) + '%',
                    { good: 10, ok: 20 }, true)}
                {getMetricBadge('Sharpe Ratio', backtest.sharpe, v => v.toFixed(2),
                    { good: 1, ok: 0.5 })}
                {getMetricBadge('Total de Trades', backtest.sells, v => v + ' trades', null)}
            </div>

            {/* Equity Curve */}
            <div className='row'>
                <EquityCurve data={backtest.equityCurve} />
            </div>

            {/* Comutador por regime (Fase 3) — ledger unificado vs buy-and-hold. Trilho
                direcional é uma REGRA (long em alta / caixa na baixa), não a IA (não reconstruível).
                Responde "a arquitetura comutadora bate o hold no ciclo?". */}
            {backtest.strategy && (
                <div className='row'>
                    <div className='col-12 mb-3'>
                        <div className={`card ${backtest.strategy.beatHold ? 'border-left-success' : 'border-left-warning'}`}>
                            <div className='card-body py-2'>
                                <div className='d-flex justify-content-between flex-wrap align-items-center mb-1'>
                                    <b>Comutador por regime vs Buy-and-Hold</b>
                                    <span className={`badge ${backtest.strategy.beatHold ? 'bg-success' : 'bg-warning text-dark'} py-1`}>
                                        {backtest.strategy.beatHold ? 'BATEU o hold' : 'NÃO bateu o hold'}
                                        {' '}({(backtest.strategy.edgeVsHoldPerc >= 0 ? '+' : '') + backtest.strategy.edgeVsHoldPerc}pp)
                                    </span>
                                </div>
                                <div className='row text-center'>
                                    {getMetricBadge('Estratégia', backtest.strategy.strategyProfitPerc, v => (v >= 0 ? '+' : '') + v.toFixed(2) + '%', { good: 0.01, ok: 0 })}
                                    {getMetricBadge('Buy & Hold', backtest.strategy.holdProfitPerc, v => (v >= 0 ? '+' : '') + v.toFixed(2) + '%', { good: 0.01, ok: 0 })}
                                    {getMetricBadge('Max Drawdown', backtest.strategy.maxDrawdownPerc, v => '-' + v.toFixed(2) + '%', { good: 10, ok: 20 }, true)}
                                    {getMetricBadge('Trocas de modo', backtest.strategy.transitions, v => v + 'x', null)}
                                </div>
                                <small className='d-block text-muted mt-1'>
                                    Tempo: {backtest.strategy.timeRangingPerc}% grade (lateral) · {backtest.strategy.timeLongPerc}% comprado (alta) · {backtest.strategy.timeCashPerc}% caixa (baixa).
                                    {' '}Trilho direcional = regra transparente (não a IA). Hold marcado a mercado.
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Grid (ledger paralelo) — capital próprio, isolado do P&L direcional acima.
                Fase 2: banda ATR-adaptativa + acoplamento por regime (só opera em RANGING,
                liquida ao sair — igual ao vivo). Quando a janela não tem RANGING classificável,
                degrada pra simulação sempre-ativa. */}
            {backtest.grids && backtest.grids.length > 0 && (
                <div className='row'>
                    <div className='col-12 mb-3'>
                        <small className='d-block text-muted mb-1'>
                            Grid (simulação paralela da grade — capital próprio, isolado do resultado direcional acima)
                        </small>
                        {backtest.grids.map((g, idx) => (
                            <div key={'g' + (g.automationId || idx)} className='card border-left-primary mb-2'>
                                <div className='card-body py-2'>
                                    <div className='d-flex justify-content-between flex-wrap align-items-center mb-1'>
                                        <b>{g.name || 'Grid'}</b>
                                        <span>
                                            <span className='badge bg-info py-1 me-1'>{g.orderType}</span>
                                            {g.adaptive
                                                ? <span className='badge bg-primary py-1 me-1' title={`Banda derivada do ATR (volatilidade): ${g.bandPercentUsed}%.`}>ATR-adaptativa {g.bandPercentUsed ? `(${g.bandPercentUsed}%)` : ''}</span>
                                                : <span className='badge bg-secondary py-1 me-1'>banda fixa {g.bandPercentUsed ? `(${g.bandPercentUsed}%)` : ''}</span>}
                                            {g.regimeGated
                                                ? <span className='badge bg-success py-1' title='Opera só em RANGING e liquida ao sair, como ao vivo.'>regime-gated</span>
                                                : <span className='badge bg-warning text-dark py-1' title='Janela sem RANGING classificável — simulação sempre-ativa.'>sempre ativo</span>}
                                        </span>
                                    </div>
                                    {g.regimeGated && (
                                        <small className='d-block text-muted mb-1'>
                                            {g.rangingPerc}% do tempo em RANGING · {g.gridSessions} {g.gridSessions === 1 ? 'sessão' : 'sessões'} de grade · {g.liquidations} {g.liquidations === 1 ? 'liquidação' : 'liquidações'} na saída
                                        </small>
                                    )}
                                    {g.error
                                        ? <span className='text-danger small'>{g.error}</span>
                                        : (
                                            <div className='row text-center'>
                                                {getMetricBadge('Resultado', g.profitUsd, v => (v >= 0 ? '+' : '') + v.toFixed(2) + ' USDT', { good: 0.01, ok: 0 })}
                                                {getMetricBadge('Resultado %', g.profitPerc, v => (v >= 0 ? '+' : '') + v.toFixed(2) + '%', { good: 0.01, ok: 0 })}
                                                {getMetricBadge('Win Rate (ciclos)', g.cycleWinRate, v => v.toFixed(1) + '%', { good: 55, ok: 45 })}
                                                {getMetricBadge('Max Drawdown', g.maxDrawdownPerc, v => '-' + v.toFixed(2) + '%', { good: 10, ok: 20 }, true)}
                                                {getMetricBadge('Compras / Vendas', g.buys, v => v + ' / ' + g.sells, null)}
                                                {getMetricBadge('Fees', g.totalFeesUsd, v => v.toFixed(2) + ' USDT', null)}
                                                {getMetricBadge('Espaçamento', g.spacingPerc, v => v.toFixed(2) + '%/nível', null)}
                                                {getMetricBadge('Sobra em base', g.baseLeftUsd, v => v.toFixed(2) + ' USDT', null)}
                                            </div>
                                        )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className='row divBacktestResults'>
                <div className='col-md-6 mb-3'>
                    <li className='list-group-item list-group-item-warning'>BUY ({backtest.buys})</li>
                    {
                        backtest.results && backtest.results.length && backtest.results.filter(r => r.side === orderSides.BUY).map(order => (
                            <li key={'o' + order.transactTime} className='list-group-item'>{getItem(order)}</li>
                        ))
                    }
                </div>
                <div className='col-md-6 mb-3'>
                    <li className='list-group-item list-group-item-success'>SELL ({backtest.sells})</li>
                    {
                        backtest.results && backtest.results.length && backtest.results.filter(r => r.side === orderSides.SELL).map(order => (
                            <li key={'o' + order.transactTime} className='list-group-item'>{getItem(order)}</li>
                        ))
                    }
                </div>
            </div>
        </React.Fragment>
    )
}

export default ResultsTable;
